import * as THREE from 'three';
import { fixtureLineModeRef } from './fixtureLineMode';

/**
 * WireMarkerGenerator - Kablo segmentleri için görsel marker oluşturur
 *
 * 150 birimden uzun kablo segmentlerinde, kablo sayısını gösteren
 * 3 adet eğik çizgi marker'ı oluşturur.
 *
 * Kullanım:
 * ```javascript
 * const markers = WireMarkerGenerator.generateMarkersForSegments(
 *   verticesRef.current,
 *   previewMarkersRef.current,
 *   wireMarkersRef.current,
 *   scene
 * );
 * ```
 */
export class WireMarkerGenerator {
  /**
   * Vektörü belirli bir açı kadar döndürür
   * @param {THREE.Vector3} vector - Döndürülecek vektör
   * @param {number} angleRad - Radyan cinsinden açı
   * @returns {THREE.Vector3} Döndürülmüş vektör
   */
  static rotateVector(vector, angleRad) {
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    return new THREE.Vector3(
      vector.x * cos - vector.y * sin,
      vector.x * sin + vector.y * cos,
      0
    );
  }

  /**
   * İki nokta arasına marker çizgisi oluşturur
   * @param {THREE.Vector3} centerPoint - Marker'ın merkez noktası
   * @param {THREE.Vector3} angledDir - Eğik yön vektörü
   * @param {number} halfLength - Çizgi yarı uzunluğu
   * @param {string} parentWireId - Wire ID (undo için)
   * @returns {THREE.Line} Marker çizgisi
   */
  static createMarkerLine(centerPoint, angledDir, halfLength, parentWireId = null) {
    const start = centerPoint.clone().add(angledDir.clone().multiplyScalar(-halfLength));
    const end = centerPoint.clone().add(angledDir.clone().multiplyScalar(halfLength));

    const geo = new THREE.BufferGeometry().setFromPoints([start, end]);
    const mat = new THREE.LineBasicMaterial({ color: 0xffffff });
    const line = new THREE.Line(geo, mat);
    line.userData.role = 'wireMarker';
    if (parentWireId) {
      line.userData.parentWireId = parentWireId;
    }

    return line;
  }

  /**
   * Bir segment için marker oluşturur
   * @param {THREE.Vector3} p1 - Segment başlangıç noktası
   * @param {THREE.Vector3} p2 - Segment bitiş noktası
   * @param {number} segmentIndex - Segment indeksi
   * @param {Array} previewMarkersRef - Mevcut preview marker'ları
   * @param {Array} wireMarkersRef - Tüm wire marker'lar
   * @param {THREE.Scene} scene - Three.js scene (yeni marker'ları eklemek için)
   * @param {string} parentWireId - Wire ID (undo için)
   * @param {number} markerCount - Marker sayısı (varsayılan 3, aydınlatma için 2)
   * @returns {Object|null} { startIdx, lines } veya null
   */
  static createSegmentMarkers(p1, p2, segmentIndex, previewMarkersRef, wireMarkersRef, scene, parentWireId = null, markerCount = 3) {
    const dir = new THREE.Vector3().subVectors(p2, p1);
    const length = dir.length();

    // 150 birimden küçük segmentler için marker oluşturma
    if (length < 150) return null;

    const normal = dir.clone().normalize();
    const angleRad = THREE.MathUtils.degToRad(45); // 45 derece açı
    const angledDir = this.rotateVector(normal, angleRad);
    const mid = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);

    // Mevcut marker'ı bul
    let existing = previewMarkersRef.find(m => m.startIdx === segmentIndex);

    const forwardOffset = normal.clone().setLength(markerCount === 2 ? 1.5 : 3); // 2 marker için 1.5, 3 marker için 3 birim
    const halfLength = 2.5; // Marker çizgi yarı uzunluğu

    // Marker pozisyonları: markerCount'a göre dinamik
    let positions;
    if (markerCount === 2) {
      // 2 marker: geri, ileri (ortası yok)
      positions = [
        mid.clone().sub(forwardOffset),
        mid.clone().add(forwardOffset),
      ];
    } else {
      // 3 marker: geri, orta, ileri
      positions = [
        mid.clone().sub(forwardOffset),
        mid.clone(),
        mid.clone().add(forwardOffset),
      ];
    }

    if (!existing) {
      // Yeni marker set'i oluştur
      const markers = positions.map(centerPoint =>
        this.createMarkerLine(centerPoint, angledDir, halfLength, parentWireId)
      );

      // Wire markers ref'e ekle VE sahneye ekle
      markers.forEach(line => {
        wireMarkersRef.push(line);
        if (scene) scene.add(line); // ✅ Sahneye ekle
      });

      return { startIdx: segmentIndex, lines: markers };
    } else {
      // Mevcut marker'ları güncelle (parentWireId de güncelle)
      positions.forEach((centerPoint, j) => {
        const marker = existing.lines[j];
        const start = centerPoint.clone().add(angledDir.clone().multiplyScalar(-halfLength));
        const end = centerPoint.clone().add(angledDir.clone().multiplyScalar(halfLength));
        marker.geometry.setFromPoints([start, end]);
        marker.visible = true;
        if (parentWireId) {
          marker.userData.parentWireId = parentWireId;
        }
      });

      return null; // Güncelleme yapıldı, yeni marker ekleme
    }
  }

  /**
   * Tüm vertex'ler için marker'ları oluştur/güncelle
   * @param {Array<THREE.Vector3>} vertices - Wire vertex'leri
   * @param {Array} previewMarkersRef - Mevcut preview marker'ları (current değeri)
   * @param {Array} wireMarkersRef - Tüm wire marker'lar (current değeri)
   * @param {THREE.Scene} scene - Three.js scene
   * @param {string} parentWireId - Wire ID (undo için)
   * @param {number} markerCount - Marker sayısı (varsayılan 3, aydınlatma için 2)
   * @returns {number} Oluşturulan/güncellenen segment sayısı
   */
  static generateMarkersForSegments(vertices, previewMarkersRef, wireMarkersRef, scene, parentWireId = null, markerCount = 3, curveMode = false, skipFirstSegment = false) {
    // ✅ Önce tüm preview marker'ları gizle — vertex < 2 veya markerCount 0 olsa BİLE.
    // (Eskiden hide erken return'lerden SONRAYDI → local undo 0-1 vertex'e inince stale
    // preview marker'lar leftover kalıyordu.)
    (previewMarkersRef || []).forEach(marker => {
      if (marker.lines && Array.isArray(marker.lines)) {
        marker.lines.forEach(line => {
          if (line) line.visible = false;
        });
      }
    });

    if (!vertices || vertices.length < 2) return 0;

    // ✅ markerCount 0 ise marker oluşturma (panel hatları için)
    if (markerCount === 0) return 0;

    // ✅ Spline hatlar (fixture/aydınlatma): marker'ları DÜZ segment ortası yerine EĞRİ
    // üzerine koy. Çizim sırasında eğri yeniden şekillendikçe (yeni vertex eklenince)
    // marker'lar her frame eğriye göre güncellenir → wire üzerinde kalır.
    if (curveMode) {
      return this.generateCurveMarkers(vertices, previewMarkersRef, wireMarkersRef, scene, parentWireId, markerCount);
    }

    let processedCount = 0;

    // Tüm segmentleri kontrol et
    for (let i = 0; i < vertices.length - 1; i++) {
      // ✅ İlk edge'i atla (panel/kaynak bağlantı segmenti) — kalıcı marker'lar (useSocket/
      // useLighting) ilk edge'i atladığı için preview de atlamalı; yoksa Enter'da ilk
      // preview marker siliniyor gibi görünüyordu (preview ≠ permanent).
      if (skipFirstSegment && i === 0) continue;

      const p1 = vertices[i];
      const p2 = vertices[i + 1];

      const newMarker = this.createSegmentMarkers(
        p1,
        p2,
        i,
        previewMarkersRef,
        wireMarkersRef,
        scene,
        parentWireId,
        markerCount
      );

      if (newMarker) {
        previewMarkersRef.push(newMarker);
        processedCount++;
      } else {
        // Güncellendi
        processedCount++;
      }
    }

    return processedCount;
  }

  /**
   * Spline (CatmullRom) eğrisi üzerinde marker yerleşimlerini hesaplar. Hem preview
   * (generateCurveMarkers) hem kalıcı marker (useFixture) AYNI fonksiyonu kullanır →
   * konumlar birebir eşleşir. Açı konvansiyonu createSegmentMarkers ile aynı: çizgiler
   * tangent'in 45° döndürülmüşü, tangent boyunca konumlandırılır.
   * @returns {Array<{point, tangent, centers:THREE.Vector3[]}>}
   */
  static curveMarkerPlacements(v3Vertices, markerCount = 2, lineMode = false) {
    const pts = (v3Vertices || []).map(v => (v.isVector3 ? v : new THREE.Vector3(v.x, v.y, v.z || 0)));
    if (pts.length < 2) return [];
    // ✅ Düz mod: eğri yerine düz segment zinciri (CurvePath+LineCurve3). getPointAt/
    // getTangentAt/getLength aynı arayüzde çalışır → aşağıdaki örnekleme değişmez.
    let curve;
    if (lineMode) {
      curve = new THREE.CurvePath();
      for (let i = 0; i < pts.length - 1; i++) {
        curve.add(new THREE.LineCurve3(pts[i], pts[i + 1]));
      }
    } else {
      curve = new THREE.CatmullRomCurve3(pts);
    }
    const curveLength = curve.getLength();
    if (!(curveLength > 0)) return [];
    const interval = 150;
    const fwdLen = markerCount === 2 ? 1.5 : 3;
    const placements = [];
    for (let d = interval; d < curveLength; d += interval) {
      const t = d / curveLength;
      const point = curve.getPointAt(t);
      const tangent = curve.getTangentAt(t).normalize();
      const forward = tangent.clone().setLength(fwdLen);
      const centers = markerCount === 2
        ? [point.clone().sub(forward), point.clone().add(forward)]
        : [point.clone().sub(forward), point.clone(), point.clone().add(forward)];
      placements.push({ point, tangent, centers });
    }
    return placements;
  }

  /**
   * KALICI eğri-bazlı marker'ları (markerGroup, sürüklenebilir) oluşturur ve sahneye ekler.
   * Hem useFixture (ilk çizim) hem FixtureCommand (redo) kullanır → tutarlı.
   * @returns {THREE.Group[]} oluşturulan markerGroup'lar
   */
  static createPermanentCurveMarkers(scene, v3Vertices, wireId, colorNum, markerCount = 2, lineMode = false) {
    const placements = this.curveMarkerPlacements(v3Vertices, markerCount, lineMode);
    const halfLength = 2.5;
    const angleRad = Math.PI / 4;
    const created = [];
    placements.forEach((pl, idx) => {
      const angled = this.rotateVector(pl.tangent, angleRad);
      const markerGroup = new THREE.Group();
      markerGroup.position.set(0, 0, 0);
      markerGroup.userData = {
        role: 'wireMarker',
        parentWireId: wireId,
        type: 'permanent',
        id: markerGroup.uuid,
        edgeIndex: idx,
        lastEdgeAngle: Math.atan2(pl.tangent.y, pl.tangent.x) + angleRad,
        midPoint: pl.point.clone(),
        isSelectable: false,
        isGizmo: false,
      };
      pl.centers.forEach((centerPoint) => {
        const start = centerPoint.clone().add(angled.clone().multiplyScalar(-halfLength));
        const end = centerPoint.clone().add(angled.clone().multiplyScalar(halfLength));
        const geo = new THREE.BufferGeometry().setFromPoints([start, end]);
        const mat = new THREE.LineBasicMaterial({ color: colorNum });
        markerGroup.add(new THREE.Line(geo, mat));
      });
      if (scene) scene.add(markerGroup);
      created.push(markerGroup);
    });
    return created;
  }

  /**
   * Preview için eğri-bazlı marker üret/güncelle (reuse: curveIdx). generateMarkersForSegments
   * curveMode=true iken çağrılır. Her frame eğri yeniden hesaplanır → spline reshape'inde
   * marker'lar wire üzerinde kalır.
   */
  static generateCurveMarkers(vertices, previewMarkersRef, wireMarkersRef, scene, parentWireId, markerCount) {
    // Düz mod (L) açıksa preview marker'ları da düz hat üzerinde (WYSIWYG).
    const placements = this.curveMarkerPlacements(vertices, markerCount, fixtureLineModeRef.current);
    const halfLength = 2.5;
    const angleRad = Math.PI / 4;
    placements.forEach((pl, idx) => {
      const angled = this.rotateVector(pl.tangent, angleRad);
      const existing = previewMarkersRef.find(m => m.curveIdx === idx);
      if (!existing) {
        const lines = pl.centers.map(c => this.createMarkerLine(c, angled, halfLength, parentWireId));
        lines.forEach(l => { wireMarkersRef.push(l); if (scene) scene.add(l); });
        previewMarkersRef.push({ curveIdx: idx, lines });
      } else {
        pl.centers.forEach((c, j) => {
          const line = existing.lines[j];
          if (!line) return;
          const s = c.clone().add(angled.clone().multiplyScalar(-halfLength));
          const e = c.clone().add(angled.clone().multiplyScalar(halfLength));
          line.geometry.setFromPoints([s, e]);
          line.visible = true;
          if (parentWireId) line.userData.parentWireId = parentWireId;
        });
      }
    });
    return placements.length;
  }

  /**
   * Tüm marker'ları sahne temizle
   * @param {Array} previewMarkersRef - Preview marker'ları (current değeri)
   * @param {THREE.Scene} scene - Three.js scene
   */
  static cleanupMarkers(previewMarkersRef, scene) {
    if (!previewMarkersRef || !scene) return;

    previewMarkersRef.forEach(markerGroup => {
      if (markerGroup.lines && Array.isArray(markerGroup.lines)) {
        markerGroup.lines.forEach(line => {
          if (line && scene) {
            scene.remove(line);
            if (line.geometry) line.geometry.dispose();
            if (line.material) line.material.dispose();
          }
        });
      }
    });

    // Array'i temizle
    previewMarkersRef.length = 0;
  }
}

export default WireMarkerGenerator;
